'use client';

import { useRouter } from 'next/compat/router';
import { NextRouter } from 'next/router';
import { useCallback } from 'react';

function deserialize_(url: string): URLSearchParams {
  const components = url.split('?');
  const search = components[1] || '';
  return new URLSearchParams(search);
}

function serialize_(url: string, params: IParams): URLSearchParams {
  const searchParams = deserialize_(url);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      searchParams.set(key, value);
    }
  }
  return searchParams;
}

function searchParamsToObject_(params: URLSearchParams): { [key: string]: string } {
  const entries = params.entries();
  const result: { [key: string]: string } = {};
  for (const [key, value] of entries) { // each 'entry' is a [key, value] tuple
    result[key] = value;
  }
  return result;
}

export function getSearchParams(url: string, key?: string): IParams | string | null {
  const params = deserialize_(url);
  const value = key ? params.get(key) : Object.fromEntries(params);
  // console.log('getSearchParams', value, params, search);
  return value;
}

export function updateSearchParams(currentPath: string, params: IParams): { pathname: string, query: { [key: string]: string } } {
  const components = currentPath.split('?');
  const searchParams = serialize_(components[1] || '', params);
  const query = searchParamsToObject_(searchParams);
  const pathname = components[0] || '';
  // console.log(pathname, query);
  return { pathname, query };
}

export function pushSearchParams(router: NextRouter, params: IParams): void {
  const { pathname, query } = updateSearchParams(router?.asPath, params);
  router?.push({ pathname, query });
}

export function pushSearchParamsSilently(router: NextRouter, params: IParams): void {
  const { pathname, query } = updateSearchParams(router?.asPath, params);
  router?.push(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export function replaceSearchParams(router: NextRouter, params: IParams): void {
  const { pathname, query } = updateSearchParams(router?.asPath, params);
  router?.replace({ pathname, query });
}

export function replaceSearchParamsSilently(router: NextRouter, params: IParams): void {
  const { pathname, query } = updateSearchParams(router?.asPath, params);
  router?.replace(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export type IParams = {
  [key: string]: string;
};

export type IUseSearchParamsResult = {
  params: IParams;
  pushParams: (params: IParams) => void;
  pushParamsSilently: (params: IParams) => void;
  replaceParams: (params: IParams) => void;
  replaceParamsSilently: (params: IParams) => void;
};

export function useSearchParams(): IUseSearchParamsResult {
  const router = useRouter();
  const params = (router ? getSearchParams(router?.asPath) : {}) as IParams;

  const pushParams = useCallback((params: IParams) => {
    if (router) {
      pushSearchParams(router, params);
    }
  }, [router]);

  const pushParamsSilently = useCallback((params: IParams) => {
    if (router) {
      pushSearchParamsSilently(router, params);
    }
  }, [router]);

  const replaceParams = useCallback((params: IParams) => {
    if (router) {
      replaceSearchParams(router, params);
    }
  }, [router]);

  const replaceParamsSilently = useCallback((params: IParams) => {
    if (router) {
      replaceSearchParamsSilently(router, params);
    }
  }, [router]);

  return { params, pushParams, pushParamsSilently, replaceParams, replaceParamsSilently };
}

export type IUseSearchParamsValueResult = Omit<IUseSearchParamsResult, 'params'> & {
  params: string | null;
};

export function useSearchParamsValue(key: string): IUseSearchParamsValueResult {
  const router = useRouter();
  const params = (router ? getSearchParams(router?.asPath, key) : null) as string | null;

  const pushParams = useCallback((params: IParams) => {
    if (router) {
      pushSearchParams(router, params);
    }
  }, [router]);

  const pushParamsSilently = useCallback((params: IParams) => {
    if (router) {
      pushSearchParamsSilently(router, params);
    }
  }, [router]);

  const replaceParams = useCallback((params: IParams) => {
    if (router) {
      replaceSearchParams(router, params);
    }
  }, [router]);

  const replaceParamsSilently = useCallback((params: IParams) => {
    if (router) {
      replaceSearchParamsSilently(router, params);
    }
  }, [router]);

  return { params, pushParams, pushParamsSilently, replaceParams, replaceParamsSilently };
}
